export default {
  name: 'group',

  component(settings) {
    return {
      componentId: Math.random().toString(36).substr(2, 9),
      renderTypeKey: 0,
      isAnimating: false,
      animationStart: false,

      // Action response configuration (toggle-only)
      actionResponseEnabled: settings.enable_action_responses || false,
      actionInitialState: settings.action_initial_state || 'hidden',
      actionCurrentState: settings.action_initial_state === 'visible' ? 'visible' : 'hidden',

      init() {
        this.$nextTick(() => {
          if (settings.animation_trigger == 'onload') {
            setTimeout(() => {
              this.animationStart = true;
            }, settings.animation_delay || 0);
          }
        });

        // Watch for screen size changes that might affect render type visibility
        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.renderTypeKey++;

            if (this.actionResponseEnabled && this.$el && this.actionCurrentState === 'visible') {
              requestAnimationFrame(() => {
                this.$el.style.opacity = '1';
              });
            }
          }, 100);
        });

        // Setup action response handlers if enabled
        if (this.actionResponseEnabled) {
          this.setupActionResponse();
        }

        // Dispatch initial trigger color state on page load
        this.$nextTick(() => {
          this.dispatchInitialTriggerColorState(0);
        });
      },

      // Setup action response functionality (toggle-only)
      setupActionResponse() {
        if (!this.actionResponseEnabled || !this.$el) return;

        const element = this.$el;
        const duration = settings.action_transition_duration;
        const easing = settings.action_transition_easing || 'ease-out';
        element.style.transition = `all ${duration}ms ${easing}`;

        // Set initial visibility state
        if (this.actionInitialState === 'hidden') {
          element.style.display = 'none';
          element.style.pointerEvents = 'none';
          element.style.opacity = '0';
          element.classList.remove('!hidden');
        } else if (this.actionInitialState === 'visible') {
          element.style.display = 'block';
          element.style.pointerEvents = 'auto';
          element.style.opacity = '1';
        }

        this.setActionState(this.actionInitialState);

        // Listen for custom event based on event_id
        const eventId = settings.event_id;
        if (eventId) {
          window.addEventListener(eventId, () => {
            this.handleActionEvent('toggle');
          });
        }

        // Setup escape key handler
        if (settings.action_close_on_escape) {
          document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.actionCurrentState === 'visible') {
              const closeCustomActions = settings.close_custom_actions;
              if (closeCustomActions && closeCustomActions.trim() !== '') {
                this.triggerCloseCustomActions(closeCustomActions, 'escape_key');
              }
              this.handleActionEvent('hide');
            }
          });
        }

        // Setup auto-close timer
        if (settings.action_auto_close_delay > 0) {
          this.$watch('actionCurrentState', (state) => {
            if (state === 'visible') {
              setTimeout(() => {
                if (this.actionCurrentState === 'visible') {
                  this.handleActionEvent('hide');
                }
              }, settings.action_auto_close_delay);
            }
          });
        }
      },

      // Handle custom event trigger (for Alpine.js event listeners)
      handleEventTrigger(event) {
        if (this.actionResponseEnabled) {
          const { type } = event;

          let interactionType = 'toggle';
          if (type.includes('show')) {
            interactionType = 'show';
          } else if (type.includes('hide')) {
            interactionType = 'hide';
          }

          this.handleActionEvent(interactionType);
        }
      },

      // Trigger custom actions from close events
      triggerCloseCustomActions(customActions, source = 'close') {
        if (!customActions || customActions.trim() === '') return;

        const eventIds = customActions.split(',');
        eventIds
          .map((eventId) => eventId.trim())
          .filter(Boolean)
          .forEach((eventId) => {
            window.dispatchEvent(
              new CustomEvent(eventId, {
                detail: {
                  source: source,
                  componentId: this.componentId,
                  timestamp: Date.now()
                }
              })
            );
          });
      },

      // Handle action events
      handleActionEvent(actionType) {
        if (this.isAnimating) return;

        switch (actionType) {
          case 'show':
            this.setActionState('visible');
            break;
          case 'hide':
            this.setActionState('hidden');
            break;
          case 'toggle':
            this.setActionState(this.actionCurrentState === 'visible' ? 'hidden' : 'visible');
            break;
        }
      },

      // Set action state with toggle animation
      setActionState(state) {
        if (!this.$el || this.actionCurrentState === state) return;

        this.isAnimating = true;
        this.actionCurrentState = state;
        const element = this.$el;

        // Dispatch trigger color event if enabled
        if (settings.enable_trigger_color_control && settings.trigger_button_selectors) {
          const triggerSelector = settings.trigger_button_selectors.trim();

          if (triggerSelector) {
            window.dispatchEvent(
              new CustomEvent('trigger-color-change', {
                detail: {
                  state: state,
                  eventId: settings.event_id,
                  triggerSelector: triggerSelector,
                  colors: {
                    activeColor: settings.trigger_active_color || '#000000',
                    inactiveColor: settings.trigger_inactive_color || '#878787'
                  },
                  timestamp: Date.now()
                }
              })
            );
          }
        }

        const duration = settings.action_transition_duration || 300;

        if (state === 'visible') {
          element.style.display = 'block';
          element.style.pointerEvents = 'auto';

          requestAnimationFrame(() => {
            element.style.opacity = '1';
            setTimeout(() => {
              this.isAnimating = false;
            }, duration);
          });
        } else {
          element.style.opacity = '0';

          setTimeout(() => {
            element.style.display = 'none';
            element.style.pointerEvents = 'none';
            this.isAnimating = false;
          }, duration);
        }
      },

      // Dispatch initial trigger color state
      dispatchInitialTriggerColorState(retryCount = 0) {
        if (!settings.enable_trigger_color_control || !settings.trigger_button_selectors) return;

        let initialState = 'visible';
        if (this.actionResponseEnabled && this.actionInitialState) {
          initialState = this.actionInitialState;
        }

        const triggerSelector = settings.trigger_button_selectors.trim();

        if (triggerSelector) {
          const matchingWrappers = document.querySelectorAll(triggerSelector);

          let hasReactButtons = false;
          matchingWrappers.forEach((wrapper) => {
            if (wrapper.querySelector('button, a')) {
              hasReactButtons = true;
            }
          });

          if (matchingWrappers.length > 0 && hasReactButtons) {
            window.dispatchEvent(
              new CustomEvent('trigger-color-change', {
                detail: {
                  state: initialState,
                  eventId: settings.event_id,
                  triggerSelector: triggerSelector,
                  colors: {
                    activeColor: settings.trigger_active_color || '#000000',
                    inactiveColor: settings.trigger_inactive_color || '#878787'
                  },
                  timestamp: Date.now(),
                  isInitial: true
                }
              })
            );
          } else if (retryCount < 3) {
            setTimeout(
              () => {
                this.dispatchInitialTriggerColorState(retryCount + 1);
              },
              500 * (retryCount + 1)
            );
          }
        }
      },

      destroy() {}
    };
  }
};
